import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../../api/client';
import { Badge, Spinner, Table, Th, Td, TrEmpty } from '../../components/ui';
import type { FeedbackFilters } from './FeedbackFilters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SortCol = 'timestamp' | 'feedbackType' | 'role' | 'score';
type SortDir = 'asc' | 'desc';

interface FeedbackItem {
  id: string;
  feedbackId?: string;
  timestamp: string;
  entityId: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  role: string;
  functionalRoles?: string[];
  feedbackType: string;
  comment?: string;
  // Normalized detail fields (provided by backend-admin)
  featureKey?: string | null;
  rating?: number | null;
  contentType?: string | null;
  contentId?: string | null;
  useful?: boolean | null;
  npsScore?: number | null;
  npsReason?: string | null;
  freeformCategory?: string | null;
  freeformMessage?: string | null;
  route?: string;
  appVersion?: string;
  sessionId?: string;
  surface?: string;
}

interface ListResponse {
  items: FeedbackItem[];
  nextCursor: string | null;
}

interface FeedbackGridProps {
  filters: FeedbackFilters;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TYPE_BADGE: Record<string, 'info' | 'warning' | 'success' | 'default'> = {
  feature_rating: 'info',
  usefulness: 'info',
  nps: 'success',
  freeform: 'default',
};

const ROLE_BADGE: Record<string, 'info' | 'warning' | 'danger' | 'default'> = {
  admin: 'danger',
  executive: 'warning',
  product_manager: 'info',
  team_lead: 'info',
};

function typeBadgeVariant(t: string): 'info' | 'warning' | 'success' | 'default' {
  return TYPE_BADGE[t] ?? 'default';
}

function roleBadgeVariant(r: string): 'info' | 'warning' | 'danger' | 'default' {
  return ROLE_BADGE[r] ?? 'default';
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function featureContent(item: FeedbackItem): string {
  if (item.feedbackType === 'feature_rating') {
    return item.featureKey ?? '—';
  }
  if (item.feedbackType === 'usefulness') {
    return item.contentType ?? '—';
  }
  if (item.feedbackType === 'nps') {
    return item.npsScore != null ? String(item.npsScore) : '—';
  }
  if (item.feedbackType === 'freeform') {
    return item.freeformCategory ?? '—';
  }
  return '—';
}

function scoreDisplay(item: FeedbackItem): React.ReactNode {
  if (item.feedbackType === 'feature_rating') {
    const r = item.rating;
    if (r == null) return <span className="text-gray-500">—</span>;
    const full = Math.round(r);
    return (
      <span className="text-amber-400 text-xs">
        {Array.from({ length: 5 }, (_, i) => (i < full ? '★' : '☆')).join('')}
      </span>
    );
  }
  if (item.feedbackType === 'nps') {
    const s = item.npsScore;
    if (s == null) return <span className="text-gray-500">—</span>;
    return (
      <span className="inline-flex items-center justify-center w-7 h-5 rounded text-xs font-medium bg-white/10 text-white">
        {s}
      </span>
    );
  }
  if (item.feedbackType === 'usefulness') {
    const u = item.useful;
    if (u == null) return <span className="text-gray-500">—</span>;
    return <span className={u ? 'text-emerald-400' : 'text-red-400'}>{u ? '👍' : '👎'}</span>;
  }
  return <span className="text-gray-500">—</span>;
}

function getComment(item: FeedbackItem): string {
  return item.comment || item.freeformMessage || item.npsReason || '';
}

function userDisplay(item: FeedbackItem): string {
  return item.userName || item.userEmail || (item.userId ? item.userId.slice(0, 8) : '—');
}

function buildParams(filters: FeedbackFilters, cursor?: string | null): string {
  const p = new URLSearchParams();
  p.set('days', String(filters.days));
  if (filters.feedbackType) p.set('feedback_type', filters.feedbackType);
  if (filters.role) p.set('role', filters.role);
  if (filters.entityId) p.set('entity_id', filters.entityId);
  if (cursor) p.set('cursor', cursor);
  return p.toString();
}

// Sort items client-side (server returns desc by timestamp only)
function sortItems(items: FeedbackItem[], col: SortCol, dir: SortDir): FeedbackItem[] {
  return [...items].sort((a, b) => {
    let av: string | number = 0;
    let bv: string | number = 0;
    if (col === 'timestamp') { av = a.timestamp; bv = b.timestamp; }
    else if (col === 'feedbackType') { av = a.feedbackType; bv = b.feedbackType; }
    else if (col === 'role') { av = a.role; bv = b.role; }
    else if (col === 'score') {
      const sa = a.feedbackType === 'feature_rating' ? (a.rating ?? -1)
               : a.feedbackType === 'nps' ? (a.npsScore ?? -1) : -1;
      const sb = b.feedbackType === 'feature_rating' ? (b.rating ?? -1)
               : b.feedbackType === 'nps' ? (b.npsScore ?? -1) : -1;
      av = sa; bv = sb;
    }
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeedbackGrid({ filters }: FeedbackGridProps) {
  const [sortCol, setSortCol] = useState<SortCol>('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [cursor, setCursor] = useState<string | null>(null);
  const [allItems, setAllItems] = useState<FeedbackItem[]>([]);
  const [prevFilters, setPrevFilters] = useState(filters);
  const [selected, setSelected] = useState<FeedbackItem | null>(null);

  // Reset pagination when filters change
  if (filters !== prevFilters) {
    setPrevFilters(filters);
    setCursor(null);
    setAllItems([]);
  }

  const { data, isFetching } = useQuery<ListResponse>({
    queryKey: ['feedback-list', filters.days, filters.feedbackType, filters.role, filters.entityId, cursor],
    queryFn: async () => {
      const params = buildParams(filters, cursor);
      return api.get<ListResponse>(`/feedback?${params}`);
    },
    placeholderData: prev => prev,
  });

  // Accumulate pages
  if (data && !isFetching) {
    const incoming = data.items ?? [];
    const existingIds = new Set(allItems.map(i => i.id));
    const newItems = incoming.filter(i => !existingIds.has(i.id));
    if (newItems.length > 0) {
      setAllItems(prev => [...prev, ...newItems]);
    }
  }

  const displayed = sortItems(allItems, sortCol, sortDir);
  const hasMore = data?.nextCursor != null;

  function handleSort(col: SortCol) {
    if (col === sortCol) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }

  function SortIcon({ col }: { col: SortCol }) {
    if (col !== sortCol) return <span className="opacity-30 ml-1">↕</span>;
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5" />;
  }

  function SortTh({ col, children }: { col: SortCol; children: React.ReactNode }) {
    return (
      <Th>
        <button
          type="button"
          onClick={() => handleSort(col)}
          className="flex items-center gap-0.5 hover:text-white transition-colors uppercase tracking-wider text-xs font-medium"
        >
          {children}
          <SortIcon col={col} />
        </button>
      </Th>
    );
  }

  return (
    <div className="mb-6">
      <Table>
        <thead>
          <tr>
            <SortTh col="timestamp">Date</SortTh>
            <Th>Entity</Th>
            <Th>User</Th>
            <SortTh col="role">Role</SortTh>
            <SortTh col="feedbackType">Type</SortTh>
            <Th>Feature / Content</Th>
            <SortTh col="score">Score</SortTh>
            <Th>Comment</Th>
          </tr>
        </thead>
        <tbody>
          {displayed.length === 0 && !isFetching && (
            <TrEmpty cols={8} message="No feedback yet for the selected filters." />
          )}
          {displayed.map(item => {
            const comment = getComment(item);
            return (
              <tr
                key={item.id}
                onClick={() => setSelected(item)}
                className="hover:bg-white/[0.04] transition-colors cursor-pointer"
              >
                <Td className="whitespace-nowrap text-xs text-gray-400">
                  {formatTimestamp(item.timestamp)}
                </Td>
                <Td>
                  <span className="font-mono text-xs text-gray-300">
                    {(item.entityId ?? '').slice(0, 8)}
                  </span>
                </Td>
                <Td>
                  <span className="text-xs text-gray-200" title={item.userEmail || item.userId}>
                    {userDisplay(item)}
                  </span>
                </Td>
                <Td>
                  <Badge variant={roleBadgeVariant(item.role)}>
                    {item.role}
                  </Badge>
                </Td>
                <Td>
                  <Badge variant={typeBadgeVariant(item.feedbackType)}>
                    {item.feedbackType.replace('_', ' ')}
                  </Badge>
                </Td>
                <Td className="text-xs text-gray-400 max-w-[120px] truncate">
                  {featureContent(item)}
                </Td>
                <Td>{scoreDisplay(item)}</Td>
                <Td className="text-xs text-gray-400 max-w-[200px] truncate">
                  <span title={comment}>
                    {comment.length > 60 ? `${comment.slice(0, 60)}…` : comment}
                  </span>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Load more / spinner */}
      <div className="flex justify-center mt-3">
        {isFetching ? (
          <Spinner className="h-4 w-4" />
        ) : hasMore ? (
          <button
            type="button"
            onClick={() => setCursor(data?.nextCursor ?? null)}
            className="text-xs text-primary hover:text-primary/80 transition-colors px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/15"
          >
            Load more
          </button>
        ) : null}
      </div>

      {selected && (
        <FeedbackDetailModal item={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal — full record, including the complete comment
// ---------------------------------------------------------------------------

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex gap-3 py-1.5 border-b border-white/[0.05] last:border-0">
      <div className="w-32 flex-shrink-0 text-xs uppercase tracking-wider text-gray-500">{label}</div>
      <div className="flex-1 text-sm text-gray-200 break-words">{value}</div>
    </div>
  );
}

function FeedbackDetailModal({ item, onClose }: { item: FeedbackItem; onClose: () => void }) {
  const comment = getComment(item);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl bg-[#15171c] border border-white/10 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Badge variant={typeBadgeVariant(item.feedbackType)}>
              {item.feedbackType.replace('_', ' ')}
            </Badge>
            <Badge variant={roleBadgeVariant(item.role)}>{item.role}</Badge>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4">
          {comment && (
            <div className="mb-4 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3">
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Comment</div>
              <div className="text-sm text-gray-100 whitespace-pre-wrap break-words">{comment}</div>
            </div>
          )}

          <DetailRow label="User" value={item.userName || '—'} />
          <DetailRow label="Email" value={item.userEmail} />
          <DetailRow label="User ID" value={<span className="font-mono text-xs">{item.userId}</span>} />
          <DetailRow label="Entity ID" value={<span className="font-mono text-xs">{item.entityId}</span>} />
          <DetailRow label="Submitted" value={formatTimestamp(item.timestamp)} />
          <DetailRow label="Score" value={scoreDisplay(item)} />
          <DetailRow label="Feature/Content" value={featureContent(item)} />
          {item.feedbackType === 'usefulness' && (
            <DetailRow label="Content ID" value={item.contentId} />
          )}
          {item.feedbackType === 'freeform' && (
            <DetailRow label="Category" value={item.freeformCategory} />
          )}
          <DetailRow label="Route" value={item.route} />
          <DetailRow label="App version" value={item.appVersion} />
          <DetailRow label="Session" value={item.sessionId && <span className="font-mono text-xs">{item.sessionId}</span>} />
          <DetailRow label="Feedback ID" value={<span className="font-mono text-xs">{item.feedbackId ?? item.id}</span>} />
        </div>
      </div>
    </div>
  );
}
